/**
 * Animation System
 * Orchestrates bouncy animations and fires audio events for future sound integration.
 */

// Sound event names for audio integration
export const SoundEvents = {
    TAP: 'tap',
    SELECT: 'select',
    DESELECT: 'deselect',
    MATCH_SUCCESS: 'match-success',
    MATCH_FAIL: 'match-fail',
    CATEGORY_COMPLETE: 'category-complete',
    FANFARE_VICTORY: 'fanfare-victory',
    DIVE_IN: 'dive-in',
    DIVE_OUT: 'dive-out',
    GAME_READY: 'game-ready',
    THEME_SWITCH: 'theme-switch'
};

/**
 * Dispatch a sound event for the audio system to handle
 */
export function emitSound(soundName) {
    window.dispatchEvent(new CustomEvent('game:sound', {
        detail: { sound: soundName }
    }));
}

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Animate grid items cascading in
 * @param {HTMLElement[]} buttons - Array of word buttons
 * @param {number} staggerMs - Delay between each button (default 25ms)
 */
export function animateGridCascade(buttons, staggerMs = 25) {
    if (prefersReducedMotion()) {
        buttons.forEach(btn => btn.style.opacity = '1');
        emitSound(SoundEvents.GAME_READY);
        return;
    }

    buttons.forEach((btn, index) => {
        btn.style.setProperty('--cascade-delay', `${index * staggerMs}ms`);
        btn.classList.add('cascade-enter');
    });

    // Emit sound when cascade completes
    const totalDuration = buttons.length * staggerMs + 250;
    setTimeout(() => emitSound(SoundEvents.GAME_READY), totalDuration);
}

/**
 * Animate header drop-in
 * @param {HTMLElement} header - Header element
 */
export function animateHeader(header) {
    if (prefersReducedMotion()) return;
    header.classList.add('header-animate');
}

/**
 * Animate instructions fade-in
 * @param {HTMLElement} instructions - Instructions element
 */
export function animateInstructions(instructions) {
    if (prefersReducedMotion()) return;
    instructions.classList.add('instructions-animate');
}

/**
 * Create particle burst effect at element position
 * @param {HTMLElement} element - Element to burst from
 * @param {string} color - Particle color
 */
export function createParticleBurst(element, color) {
    if (prefersReducedMotion()) return;

    const rect = element.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Inner ring - 6 particles, faster, closer
    for (let i = 0; i < 6; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';

        const angle = (Math.PI * 2 * i) / 6 + (Math.random() - 0.5) * 0.3;
        const distance = 35 + Math.random() * 20;
        const tx = Math.cos(angle) * distance;
        const ty = Math.sin(angle) * distance;

        particle.style.cssText = `
            left: ${centerX}px;
            top: ${centerY}px;
            background-color: ${color};
            color: ${color};
            --tx: ${tx}px;
            --ty: ${ty}px;
            --particle-duration: 400ms;
            --end-scale: 0.5;
        `;

        document.body.appendChild(particle);
        setTimeout(() => particle.remove(), 450);
    }

    // Outer ring - 8 particles, stars, larger distance
    for (let i = 0; i < 8; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle star';

        const angle = (Math.PI * 2 * i) / 8 + Math.PI / 8; // Offset from inner ring
        const distance = 55 + Math.random() * 35;
        const tx = Math.cos(angle) * distance;
        const ty = Math.sin(angle) * distance;

        particle.style.cssText = `
            left: ${centerX}px;
            top: ${centerY}px;
            color: ${color};
            --tx: ${tx}px;
            --ty: ${ty}px;
            --particle-delay: 50ms;
            --particle-duration: 550ms;
            --star-size: ${14 + Math.random() * 6}px;
            --end-scale: 0.3;
        `;

        document.body.appendChild(particle);
        setTimeout(() => particle.remove(), 650);
    }
}

/**
 * Animate correct match with jiggle, particles, and flash
 * @param {HTMLElement} button1 - First matched button
 * @param {HTMLElement} button2 - Second matched button
 * @param {string} categoryColor - Color for particles
 */
export function animateMatchSuccess(button1, button2, categoryColor) {
    emitSound(SoundEvents.MATCH_SUCCESS);

    if (prefersReducedMotion()) return;

    [button1, button2].forEach(btn => {
        // Add flash effect with category color
        btn.style.setProperty('--flash-color', categoryColor + '80'); // 50% opacity
        btn.classList.add('match-animation', 'match-flash');

        // Create particle burst
        createParticleBurst(btn, categoryColor);

        btn.addEventListener('animationend', (e) => {
            if (e.animationName === 'match-jiggle-bouncy') {
                btn.classList.remove('match-animation', 'match-flash');
            }
        }, { once: true });
    });
}

/**
 * Animate wrong match with improved shake
 * @param {HTMLElement} button1 - First button
 * @param {HTMLElement} button2 - Second button
 */
export function animateMatchFail(button1, button2) {
    emitSound(SoundEvents.MATCH_FAIL);

    if (prefersReducedMotion()) return;

    [button1, button2].forEach(btn => {
        btn.classList.add('shake');

        btn.addEventListener('animationend', () => {
            btn.classList.remove('shake');
        }, { once: true });
    });
}

/**
 * Animate selection with pop
 * @param {HTMLElement} button - Button being selected
 */
export function animateSelect(button) {
    emitSound(SoundEvents.SELECT);
    // CSS handles the animation via .selected class
}

/**
 * Animate deselection with deflate
 * @param {HTMLElement} button - Button being deselected
 */
export function animateDeselect(button) {
    emitSound(SoundEvents.DESELECT);

    if (prefersReducedMotion()) return;

    button.classList.add('deselecting');
    button.addEventListener('animationend', () => {
        button.classList.remove('deselecting');
    }, { once: true });
}

/**
 * Animate text with letter-by-letter pop
 * @param {HTMLElement} element - Element containing text to animate
 * @param {number} delayPerLetter - Delay between letters in ms (default 30)
 */
export function animateLetterPop(element, delayPerLetter = 30) {
    if (prefersReducedMotion()) return;

    const text = element.textContent;
    element.textContent = '';

    [...text].forEach((char, index) => {
        const span = document.createElement('span');
        span.textContent = char === ' ' ? '\u00A0' : char;
        span.className = 'letter-pop';
        span.style.setProperty('--letter-delay', `${index * delayPerLetter}ms`);
        element.appendChild(span);
    });
}

/**
 * Show category complete notification with bouncy animation
 * @param {HTMLElement} notification - Notification element
 * @param {HTMLElement} categoryNameEl - Category name element to animate
 */
export function showCategoryNotification(notification, categoryNameEl) {
    emitSound(SoundEvents.CATEGORY_COMPLETE);

    notification.classList.remove('hidden');

    if (!prefersReducedMotion()) {
        animateLetterPop(categoryNameEl);
    }
}

/**
 * Hide category notification with bounce-out
 * @param {HTMLElement} notification - Notification element
 */
export function hideCategoryNotification(notification) {
    if (prefersReducedMotion()) {
        notification.classList.add('hidden');
        return;
    }

    const content = notification.querySelector('.notification-content');
    content.classList.add('exiting');

    content.addEventListener('animationend', () => {
        notification.classList.add('hidden');
        content.classList.remove('exiting');
    }, { once: true });
}

/**
 * Create enhanced confetti with varied shapes
 * @param {number} count - Number of confetti pieces
 * @param {string[]} colors - Array of category colors to use
 */
export function createConfetti(count = 50, colors = null) {
    if (prefersReducedMotion()) return;

    const defaultColors = [
        '#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5',
        '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50'
    ];
    const confettiColors = colors || defaultColors;
    const shapes = ['shape-rect', 'shape-circle', 'shape-triangle', 'shape-squiggle'];

    for (let i = 0; i < count; i++) {
        const confetti = document.createElement('div');
        const colorIndex = Math.floor(Math.random() * confettiColors.length);
        const shape = shapes[Math.floor(Math.random() * shapes.length)];

        confetti.className = `confetti color-${colorIndex % 10} ${shape}`;

        // Random position and timing
        const startX = Math.random() * window.innerWidth;
        const drift = (Math.random() - 0.5) * 100;
        const driftEnd = drift + (Math.random() - 0.5) * 50;
        const duration = 2 + Math.random() * 2;
        const delay = Math.random() * 500;

        confetti.style.cssText = `
            left: ${startX}px;
            top: -20px;
            --drift: ${drift}px;
            --drift-end: ${driftEnd}px;
            --confetti-duration: ${duration}s;
            --confetti-delay: ${delay}ms;
        `;

        document.body.appendChild(confetti);

        // Remove after animation
        setTimeout(() => confetti.remove(), (duration * 1000) + delay + 100);
    }
}

/**
 * Create floating sparkles effect
 * @param {number} count - Number of sparkles
 * @param {number} durationMs - How long to spawn sparkles
 */
export function createFloatingSparkles(count = 20, durationMs = 3000) {
    if (prefersReducedMotion()) return;

    const sparkleEmojis = ['✨', '⭐', '💫', '🌟'];
    const interval = durationMs / count;

    for (let i = 0; i < count; i++) {
        setTimeout(() => {
            const sparkle = document.createElement('div');
            sparkle.className = 'floating-sparkle';
            sparkle.textContent = sparkleEmojis[Math.floor(Math.random() * sparkleEmojis.length)];
            sparkle.style.cssText = `
                left: ${Math.random() * window.innerWidth}px;
                bottom: ${Math.random() * 100}px;
                --sparkle-delay: ${Math.random() * 200}ms;
            `;

            document.body.appendChild(sparkle);
            setTimeout(() => sparkle.remove(), 2200);
        }, i * interval);
    }
}

/**
 * Show victory screen with full celebration
 * @param {HTMLElement} victoryScreen - Victory screen element
 * @param {HTMLElement[]} categoryElements - Category list elements
 * @param {string[]} categoryColors - Colors of completed categories
 */
export function showVictory(victoryScreen, categoryElements, categoryColors) {
    emitSound(SoundEvents.FANFARE_VICTORY);

    // Screen pulse
    if (!prefersReducedMotion()) {
        document.body.classList.add('victory-pulse');
        setTimeout(() => document.body.classList.remove('victory-pulse'), 300);
    }

    // Show victory screen
    victoryScreen.classList.remove('hidden');
    victoryScreen.classList.add('animate-in');

    // Staggered category reveal
    if (!prefersReducedMotion()) {
        categoryElements.forEach((el, index) => {
            el.style.setProperty('--category-delay', `${300 + index * 100}ms`);
            el.classList.add('animate-in');
        });
    }

    // Confetti explosion
    createConfetti(60, categoryColors);
    setTimeout(() => createConfetti(40, categoryColors), 300);
    setTimeout(() => createConfetti(30, categoryColors), 600);

    // Floating sparkles
    createFloatingSparkles(25, 4000);
}

/**
 * Animate theme toggle with circular wipe
 * @param {HTMLElement} toggleButton - Theme toggle button
 * @param {boolean} isDark - Whether switching to dark mode
 */
export function animateThemeToggle(toggleButton, isDark) {
    emitSound(SoundEvents.THEME_SWITCH);

    if (prefersReducedMotion()) return;

    const rect = toggleButton.getBoundingClientRect();
    const x = ((rect.left + rect.width / 2) / window.innerWidth) * 100;
    const y = ((rect.top + rect.height / 2) / window.innerHeight) * 100;

    document.body.style.setProperty('--wipe-x', `${x}%`);
    document.body.style.setProperty('--wipe-y', `${y}%`);
    document.body.style.setProperty('--theme-wipe-color', isDark ? '#1a1a1a' : '#fafafa');

    document.body.classList.add('theme-transitioning');

    setTimeout(() => {
        document.body.classList.remove('theme-transitioning');
    }, 500);
}

/**
 * Animate an element flying to a stack area in an arc motion
 * @param {HTMLElement} element - Element to animate
 * @param {HTMLElement} targetStack - Target stack element to fly to
 * @param {Object} options - Animation options
 * @param {number} options.duration - Animation duration in ms (default 400)
 * @returns {Promise} Resolves when animation completes
 */
export async function animateToStack(element, targetStack, options = {}) {
    const duration = options.duration || 400;

    // Skip animation if reduced motion preferred
    if (prefersReducedMotion()) {
        return Promise.resolve();
    }

    // Get positions
    const startRect = element.getBoundingClientRect();
    const endRect = targetStack.getBoundingClientRect();

    // Create flying clone
    const clone = element.cloneNode(true);
    clone.classList.add('arc-flying');
    clone.style.position = 'fixed';
    clone.style.left = startRect.left + 'px';
    clone.style.top = startRect.top + 'px';
    clone.style.width = startRect.width + 'px';
    clone.style.height = startRect.height + 'px';
    document.body.appendChild(clone);

    // Calculate movement
    const deltaX = endRect.left + endRect.width / 2 - startRect.left - startRect.width / 2;
    const deltaY = endRect.top - startRect.top;

    // Animate using Web Animations API
    // Note: translate must come BEFORE scale, otherwise scale affects the translate values
    return new Promise(resolve => {
        const animation = clone.animate([
            { transform: 'translate(0, 0) scale(1)', opacity: 1 },
            { transform: `translate(${deltaX / 2}px, -60px) scale(0.7)`, opacity: 1 },
            { transform: `translate(${deltaX}px, ${deltaY}px) scale(0.5)`, opacity: 0.8 }
        ], {
            duration,
            easing: 'ease-in-out',
            fill: 'forwards'
        });

        animation.onfinish = () => {
            clone.remove();
            resolve();
        };
    });
}

/**
 * Capture grid button positions before re-render (FLIP: First step)
 * @param {HTMLElement} container - Grid container
 * @param {number[]} indicesToRemove - Indices of buttons being removed
 * @returns {Object} Position data for FLIP animation
 */
export function captureGridPositions(container, indicesToRemove) {
    const buttons = [...container.querySelectorAll('.word-button')];
    const positions = new Map();

    buttons.forEach(btn => {
        const index = parseInt(btn.dataset.index);
        // Only capture positions for buttons that will remain
        if (!indicesToRemove.includes(index)) {
            const rect = btn.getBoundingClientRect();
            positions.set(index, { x: rect.left, y: rect.top });
        }
    });

    return positions;
}

/**
 * Play grid collapse animation using FLIP technique (Invert + Play steps)
 * Animation moves items UP first, then horizontally toward center
 * @param {HTMLElement} container - Grid container
 * @param {Map} oldPositions - Positions captured before re-render
 * @param {Object} options - Animation options
 * @param {number} options.duration - Animation duration in ms (default 300)
 */
export function playGridCollapse(container, oldPositions, options = {}) {
    const duration = options.duration || 300;

    if (prefersReducedMotion() || !oldPositions || oldPositions.size === 0) {
        return;
    }

    const buttons = [...container.querySelectorAll('.word-button')];
    const animatingButtons = [];

    buttons.forEach(btn => {
        const index = parseInt(btn.dataset.index);
        const oldPos = oldPositions.get(index);

        if (oldPos) {
            const newRect = btn.getBoundingClientRect();
            const deltaX = oldPos.x - newRect.left;
            const deltaY = oldPos.y - newRect.top;

            // Only animate if position actually changed
            if (Math.abs(deltaX) > 1 || Math.abs(deltaY) > 1) {
                animatingButtons.push({ btn, deltaX, deltaY });
            }
        }
    });

    if (animatingButtons.length === 0) return;

    // Use Web Animations API for two-phase animation: UP first, then horizontal
    animatingButtons.forEach(({ btn, deltaX, deltaY }) => {
        // Keyframes: start at old position → move vertically → move horizontally to final
        const keyframes = [
            { transform: `translate(${deltaX}px, ${deltaY}px)` },  // Old position
            { transform: `translate(${deltaX}px, 0px)`, offset: 0.6 },  // Same X, final Y (moved up)
            { transform: 'translate(0px, 0px)' }  // Final position
        ];

        btn.animate(keyframes, {
            duration: duration,
            easing: 'ease-out',
            fill: 'forwards'
        }).onfinish = () => {
            // Clean up by removing any inline transforms
            btn.style.transform = '';
        };
    });
}

/**
 * Initialize page load animations
 */
export function initPageAnimations() {
    const header = document.querySelector('header');
    const instructions = document.querySelector('.instructions:not(.hidden)');
    const mainGrid = document.querySelector('#main-board .main-grid');

    if (header) animateHeader(header);
    if (instructions) animateInstructions(instructions);

    // Grid cascade will be called when grid is rendered
}

// Export default object for convenience
export default {
    SoundEvents,
    emitSound,
    prefersReducedMotion,
    animateGridCascade,
    animateHeader,
    animateInstructions,
    createParticleBurst,
    animateMatchSuccess,
    animateMatchFail,
    animateSelect,
    animateDeselect,
    animateLetterPop,
    showCategoryNotification,
    hideCategoryNotification,
    createConfetti,
    createFloatingSparkles,
    showVictory,
    animateThemeToggle,
    animateToStack,
    captureGridPositions,
    playGridCollapse,
    initPageAnimations
};
